import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Polygon, OverlayView, Marker, Circle } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { visibleZonesMapSelector, zoneDetailViewSelector } from '../../../features/places/placesSlice';
const iconToolsColor = "Silver";
//const activeIconToolsColor = "Tomato";

// Helper function to determine if a color is bright or dark
// Returns true if the color is bright (should use dark text)
// Returns false if the color is dark (should use light text)
const isColorBright = (hexColor) => {
    // Default to dark if invalid color
    if (!hexColor || typeof hexColor !== 'string') return false;

    // Convert hex to RGB
    let hex = hexColor.replace('#', '');
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate luminance - colors with luminance > 0.5 are bright
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6; // Higher threshold for better readability
};

const options = {
    fillColor: "lightblue",
    fillOpacity: 0.3,
    strokeColor: "red",
    strokeOpacity: 1,
    strokeWeight: 2,
    clickable: false,
    draggable: false,
    editable: false,
    geodesic: false,
    zIndex: 1
}

const ZonePolygon = ({ zoneId, map }) => {
    const zoneData = useSelector(state => state.places.zones[`${zoneId}`]);
    const visibleZonesMap = useSelector(visibleZonesMapSelector);
    const zoneDetailView = useSelector(zoneDetailViewSelector);
    const visible = visibleZonesMap.indexOf(zoneId) !== -1;
    const [showLabel, setshowLabel] = useState(false);
    const [showPolygon, setshowPolygon] = useState(true); // Show polygon by default
    const [isInViewport, setIsInViewport] = useState(false);
    const boundsRef = useRef(null);

    useEffect(() => {
        if (zoneId === zoneDetailView) {
            setshowLabel(true);
        }
    }, [zoneDetailView, zoneId]);

    useEffect(() => {
        if (visibleZonesMap.length < 6) {
            setshowLabel(true);
        }
    }, [visibleZonesMap])

    // Ensure polygon is shown when zone is visible
    useEffect(() => {
        if (visible) {
            setshowPolygon(true);
        }
    }, [visible]);

    useEffect(() => {
        if (map && zoneData && window.google) {
            // Create zone bounds
            const zoneBounds = new window.google.maps.LatLngBounds();
            zoneData.zone_coords.forEach(p =>
                zoneBounds.extend(new window.google.maps.LatLng(parseFloat(p.lat), parseFloat(p.lng)))
            );
            boundsRef.current = zoneBounds;

            // Check if in viewport on initial render
            checkIfInViewport();

            // Add listeners for map movements
            const boundsChangedListener = map.addListener('bounds_changed', checkIfInViewport);
            const zoomChangedListener = map.addListener('zoom_changed', checkIfInViewport);
            const dragendListener = map.addListener('dragend', checkIfInViewport);

            return () => {
                // Clean up listeners
                if (window.google) {
                    window.google.maps.event.removeListener(boundsChangedListener);
                    window.google.maps.event.removeListener(zoomChangedListener);
                    window.google.maps.event.removeListener(dragendListener);
                }
            };
        }
    }, [map, zoneData]);

    const checkIfInViewport = () => {
        if (map && boundsRef.current) {
            const mapBounds = map.getBounds();
            if (mapBounds) {
                // Check if zone is in viewport
                const inView = mapBounds.intersects(boundsRef.current);
                setIsInViewport(inView);
            }
        }
    };

    const onZoneClick = (e) => {
        setshowLabel(!showLabel);
        setshowPolygon(!showPolygon);
    }

    if (zoneData && visible && window.google && (isInViewport || zoneId === zoneDetailView)) {
        const labelCenter = () => {
            const bound = new window.google.maps.LatLngBounds();
            zoneData.zone_coords.forEach(p => bound.extend(new window.google.maps.LatLng(parseFloat(p.lat), parseFloat(p.lng))));
            //console.log("Labelm", bound.getCenter().lat());
            return bound.getCenter();
        }

        const zonePath = () => {
            let path = [];
            zoneData.zone_coords.forEach(z => {
                path.push({
                    lat: parseFloat(z.lat),
                    lng: parseFloat(z.lng),
                })
            });
            //console.log("Zone path for zone", zoneId, path);
            return path;
        }

        return (
            <Fragment>
                {/* {console.log("Rendering zone", zoneId, "showPolygon:", showPolygon, "visible:", visible)} */}
                {showPolygon && <Polygon
                    paths={zonePath()}
                    options={{
                        ...options,
                        fillColor: zoneData.zone_color,
                        strokeColor: zoneData.zone_color,
                        clickable: true,
                    }}
                    onClick={onZoneClick}
                />}
                <Marker
                    icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: 'yellow',
                        fillOpacity: 0.75,
                        strokeColor: 'red',
                        strokeWeight: 2,
                        scale: 6
                    }}
                    position={{
                        lat: labelCenter().lat(),
                        lng: labelCenter().lng(),
                    }}
                    onClick={onZoneClick}
                />

                <OverlayView
                    position={{
                        lat: labelCenter().lat(),
                        lng: labelCenter().lng(),
                    }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}

                >
                    <button
                        onClick={onZoneClick}
                        style={{
                            backgroundColor: `${zoneData.zone_color || '#CF3827'}DD`,
                            position: 'absolute',
                            left: 10,
                            top: -16,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: isColorBright(zoneData.zone_color || '#CF3827') ? '#000' : '#fff',
                            border: `2px solid ${zoneData.zone_color || '#CF3827'}`,
                            maxWidth: '150px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            transform: 'translateX(-50%)',
                            transition: 'opacity 0.2s ease-in-out',
                            opacity: showLabel ? 1 : 0,
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                            outline: 'none',
                            textAlign: 'center',
                        }}>
                        {showLabel ? `${zoneData.zone_name}` : ""}
                    </button>
                </OverlayView>
            </Fragment>
        );
    } else {
        return null;
    }
}

export default React.memo(ZonePolygon);

/**
 *
 <Tag color={zoneData.zone_color}>
                    <span style={{ color: 'white' }}>
                        {zoneData.zone_name}
                    </span>
                </Tag>
 */